// UC12 — the screen every authenticated user meets when MPass hands SIMSM more
// than one role. Rebuilt to `wireframes/react/src/pages/Rol.tsx` (read-only):
// an identity card (avatar, the user's name, the MPass line with the masked
// IDNP) over a radiogroup of role cards, and ONE full-width continue action —
// not a button per row, which is what the 2026-08-30 audit found here (§4
// row 1).
import { useCallback, useMemo, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudAvatar,
  MudButton,
  MudIcon,
  MudInfoBox,
  MudRadio,
  MudRadioGroup,
  MudTag,
} from '@egov-moldova/mud-react';
import { isObserverRole, type SessionUser } from '@simsm/shared-types';
import { applyRoleSwitch } from '../app/active-registrar';
import { apiClient } from '../app/api-client';
import { homePathFor } from '../app/role-home';
import { scopeKeyFor } from '../app/role-reach';
import { useScopedRoleLabel } from '../hooks/use-observer-roles';
import { useSession } from '../hooks/use-session';

// An IDNP is a personal identifier and this card can sit on a shared desk, so
// only the first four and the last three digits are shown, which is the mask
// the wireframe draws.
function maskIdnp(idnp: string): string {
  if (idnp.length <= 7) return idnp;
  return `${idnp.slice(0, 4)}${'•'.repeat(idnp.length - 7)}${idnp.slice(-3)}`;
}

export function RoleSelectPage(): JSX.Element {
  const { t } = useTranslate();
  const roleLabel = useScopedRoleLabel();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionQuery = useSession();
  const session = sessionQuery.data;

  const roles = useMemo(() => session?.availableRoles ?? [], [session?.availableRoles]);
  // The active role is the pre-selection, so the card opens on "what you are
  // now" and Continue is a safe default. `picked` stays null until the reader
  // chooses something else.
  const [picked, setPicked] = useState<string | null>(null);
  const selected = picked ?? session?.activeRole ?? roles[0] ?? null;

  const switchRole = useMutation({
    mutationFn: async (role: string) => {
      const { data } = await apiClient.patch<SessionUser>('/auth/active-role', {
        activeRole: role,
      });
      return data;
    },
    onSuccess: (next) => {
      applyRoleSwitch(queryClient, next);
      navigate(homePathFor(next.activeRole), { replace: true });
    },
  });

  // `mud-radio-group` (MUD 1.2) owns the radiogroup keyboard contract: one Tab
  // stop, Arrow keys move the selection and focus (wrapping), Space selects. It
  // finds the radios as descendants, so the card wrappers stay as they are. It
  // has no Home/End, which this page has always answered, so those two keys are
  // the only handling left here: they jump to the first or last enabled option.
  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Home' && event.key !== 'End') return;
    const radios = Array.from(event.currentTarget.querySelectorAll('mud-radio')).filter(
      (radio) => !radio.disabled,
    );
    const target = event.key === 'Home' ? radios[0] : radios[radios.length - 1];
    if (!target?.value) return;
    event.preventDefault();
    setPicked(target.value);
    target.focus();
  }, []);

  if (!session) {
    return (
      <main className="simsm-role-select">
        <p>{t('common.loading')}</p>
      </main>
    );
  }

  const fullName = `${session.firstName} ${session.lastName}`;
  const activeRole = session.activeRole;

  // What this card may say about a role's organisation is bounded by what the
  // session actually carries. AMDM is the System Administrator's institution by
  // definition; an observer role is named after its institution in the role
  // directory; a Registrar role is named by `registrarNames`, which owner
  // decision D5 put on the session index-aligned with `registrarScope`
  // (docs/decizii-paritate-wireframe-2026-09-05.md). This row used to print the
  // raw IDNO instead — the wireframe writes the Registrar's name there
  // (`ROLES[...].org` in wireframes/react/src/lib/session.ts) and a reader
  // cannot tell one 13-digit number from another. The fallback is SidebarAccount's:
  // an IDNO the catalog does not know keeps its place in the array as an empty
  // string, so the IDNO is shown rather than a blank, because "I do not know this
  // Registrar" and "this Registrar is withdrawn" are different sentences.
  // Nothing on this line is guessed, and a line that would only repeat the role's
  // own name is not drawn at all (an observer role is usually named after its
  // institution). Each card reads its own role's scope (caiet UC10-04): the
  // flat fields are only the active role's.
  const organisationFor = (role: string, label: string): string => {
    const registrarScope = session.scopes?.[role]?.registrarScope ?? [];
    const registrarNames = session.scopes?.[role]?.registrarNames ?? [];
    const organisation = ((): string => {
      if (role === 'SYSTEM_ADMIN') return t('org.amdm');
      if (isObserverRole(role)) {
        const named = roleLabel(role, undefined);
        return named === role ? '' : named;
      }
      if (registrarScope.length === 1) {
        return registrarNames[0] || registrarScope[0] || '';
      }
      // More than one Registrar names none of them: the row is one line, and
      // a count is the honest summary. It prints no identifier either way.
      if (registrarScope.length > 1) {
        return t('role_select.org.registrar_count', { count: registrarScope.length });
      }
      return '';
    })();
    return organisation === label ? '' : organisation;
  };

  return (
    <main className="simsm-role-select">
      <div className="simsm-role-select__card">
        <header className="simsm-role-select__identity">
          <MudAvatar type="initials" size="lg" name={fullName} alt="" />
          <h1 className="simsm-role-select__name">{fullName}</h1>
          <p className="simsm-role-select__auth">
            {t('role_select.identity.mpass', { idnp: maskIdnp(session.idnp) })}
          </p>
        </header>

        <div className="simsm-role-select__body">
          <h2 className="simsm-role-select__group-label">{t('role_select.group.label')}</h2>

          {/* The heading above is the visible label; the group carries the same
              text as its accessible name (MUD moves `aria-label` onto its
              internal radiogroup). */}
          <MudRadioGroup
            aria-label={t('role_select.group.label')}
            className="simsm-role-select__list"
            {...(selected ? { value: selected } : {})}
            onMudChange={(event: CustomEvent<{ value: string | undefined }>) => {
              if (event.detail.value) setPicked(event.detail.value);
            }}
            onKeyDown={handleKeyDown}
          >
            {roles.map((role) => {
              const isSelected = role === selected;
              // Named by its own scope's Registrar types (caiet UC10-02, CF-26), so
              // the card and the account block read the same IMSP variant
              // (docs/shape-ops-role-label-imsp.md §2).
              const kinds =
                session.scopes?.[role]?.registrarKinds ??
                (role === activeRole ? session.registrarKinds : undefined);
              const label = roleLabel(role, kinds);
              const meta = [
                organisationFor(role, label),
                role === activeRole ? t('role_select.current') : '',
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <div
                  key={role}
                  className={`simsm-role-select__option${isSelected ? ' is-selected' : ''}`}
                >
                  <MudRadio value={role}>
                    {/* The scope tag rides inside the label slot on purpose: it
                        makes the whole card the radio's own click target, and it
                        folds the scope into the option's accessible name, so the
                        reach of a role is announced together with it. */}
                    <span slot="label" className="simsm-role-select__option-label">
                      <span className="simsm-role-select__option-name" title={label}>
                        {label}
                      </span>
                      {/* A whitespace-only text node between the two: it is not
                          rendered as a flex item, and it is what keeps the role
                          name and the scope from being flattened into one word
                          when `mud-radio` builds the accessible name. */}{' '}
                      {/* The scope rides in the tag's default slot, not its
                          `label` prop: a prop is painted inside mud-tag's shadow
                          root, where `mud-radio` cannot read it when it flattens
                          the slotted label into the option's accessible name. */}
                      <MudTag variant="info" size="sm" type="outlined" semantic="neutral">
                        {t(scopeKeyFor(role))}
                      </MudTag>
                    </span>
                    {meta ? (
                      <span
                        slot="supporting-text"
                        className="simsm-role-select__option-meta"
                        title={meta}
                      >
                        {meta}
                      </span>
                    ) : null}
                  </MudRadio>
                  {/* MUD's selection indicator, always present so the row does
                      not reflow when the choice moves. */}
                  <span
                    className="simsm-role-select__option-check"
                    data-checked={isSelected ? 'true' : 'false'}
                    aria-hidden="true"
                  >
                    <MudIcon name="checkmark-small" size={20} />
                  </span>
                </div>
              );
            })}
          </MudRadioGroup>

          {switchRole.isError ? (
            <MudInfoBox emphasis="strong" role="alert" variant="error">
              {t('role_select.error')}
            </MudInfoBox>
          ) : null}

          <MudButton
            shape="circular"
            className="simsm-role-select__continue"
            variant="primary"
            size="lg"
            fullWidth
            disabled={switchRole.isPending || selected === null}
            onClick={() => {
              if (selected) switchRole.mutate(selected);
            }}
          >
            {t('role_select.action.continue')}
            <MudIcon slot="icon-end" name="arrow-right" size={20} />
          </MudButton>

          <p className="simsm-role-select__hint">{t('role_select.hint')}</p>
        </div>
      </div>
    </main>
  );
}
